import React, { useEffect } from 'react';
import AddressSuggestInput from './AddressSuggestInput';
import ActivityTypeSelect from './ActivityTypeSelect';
import { AIRLINES } from '../utils/airlines';
import {
  addDays,
  cityKeyFromText,
  diffDays,
  formatDurationMinutes,
  openNativePicker,
  toMinutes,
} from '../utils/travelLegs';

export default function TravelRoutingEditor({
  draft,
  setDraft,
  activityTypes,
  categoryId,
  fallbackDate,
  onTypeCreated,
}) {
  const safeDraft = draft || {};
  const useLegs = !!safeDraft.use_legs;
  const legs = Array.isArray(safeDraft.legs) ? safeDraft.legs : [];

  const typeNameById = (typeId) => {
    const t = (Array.isArray(activityTypes) ? activityTypes : []).find(x => String(x.type_id) === String(typeId));
    return t ? String(t.type_name || '') : '';
  };

  const ensureDefaultLeg = (cur) => {
    const date = String(fallbackDate || cur.date || '').slice(0, 10);
    return {
      leg_index: 0,
      type_id: String(cur.type_id || ''),
      airline: cur.airline || '',
      from_address: cur.from_address || '',
      from_lat: cur.from_lat ?? null,
      from_lon: cur.from_lon ?? null,
      from_city: cur.from_city || '',
      to_address: cur.to_address || '',
      to_lat: cur.to_lat ?? null,
      to_lon: cur.to_lon ?? null,
      to_city: cur.to_city || '',
      depart_date: date,
      depart_time: cur.start_time || '',
      arrive_day_offset: 0,
      arrive_time: cur.end_time || '',
    };
  };

  const isFlightLeg = (typeId) => {
    const name = typeNameById(typeId).trim().toLowerCase();
    return name === 'flight';
  };

  useEffect(() => {
    if (!useLegs) return;
    setDraft((prev) => {
      const cur = prev || {};
      if (!cur.use_legs) return cur;
      const curLegs = Array.isArray(cur.legs) ? cur.legs : [];
      if (!curLegs.length) return cur;
      const first = curLegs[0] || {};
      const last = curLegs[curLegs.length - 1] || {};
      const nextStart = String(first?.depart_time || '').slice(0, 5);
      const nextEnd = String(last?.arrive_time || '').slice(0, 5);
      const changed = (nextStart && cur.start_time !== nextStart) || (nextEnd && cur.end_time !== nextEnd);
      if (!changed) return cur;
      return {
        ...cur,
        ...(nextStart ? { start_time: nextStart } : {}),
        ...(nextEnd ? { end_time: nextEnd } : {}),
      };
    });
  }, [useLegs, setDraft]);

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <label className="tp-row" style={{ gap: 8, color: 'var(--tp-muted)' }}>
        <input
          type="checkbox"
          checked={useLegs}
          onChange={(e) => {
            const checked = e.target.checked;
            setDraft((prev) => {
              const cur = prev || {};
              const existing = Array.isArray(cur.legs) ? cur.legs : [];
              const nextLegs = checked
                ? (existing.length ? existing : [ensureDefaultLeg(cur)])
                : existing;
              return { ...cur, use_legs: checked, legs: nextLegs };
            });
          }}
        />
        <span style={{ fontSize: 12 }}>Connections/layovers?</span>
      </label>

      {useLegs ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {legs.map((leg, idx) => (
            <div key={idx} className="tp-card tp-card--tight" style={{ padding: 10 }}>
              <div className="tp-row" style={{ justifyContent: 'space-between' }}>
                <span className="tp-pill">Leg {idx + 1}</span>
                <button
                  type="button"
                  className="tp-btn--danger"
                  disabled={legs.length <= 1}
                  onClick={() => setDraft(prev => {
                    const cur = prev || {};
                    const nextLegs = (Array.isArray(cur.legs) ? cur.legs : []).filter((_, i) => i !== idx);
                    return { ...cur, legs: nextLegs };
                  })}
                >
                  Remove
                </button>
              </div>

              <div className="tp-grid" style={{ gap: 10, marginTop: 10 }}>
                <div className="tp-col-6">
                  <ActivityTypeSelect
                    types={activityTypes}
                    categoryId={categoryId}
                    typeId={leg?.type_id || safeDraft.type_id}
                    onTypeChange={v => setDraft(prev => {
                      const cur = prev || {};
                      const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                      nextLegs[idx] = { ...nextLegs[idx], type_id: v };
                      return { ...cur, legs: nextLegs };
                    })}
                    onTypeCreated={newType => {
                      onTypeCreated?.(newType);
                      setDraft(prev => {
                        const cur = prev || {};
                        const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                        nextLegs[idx] = { ...nextLegs[idx], type_id: newType.type_id };
                        return { ...cur, legs: nextLegs };
                      });
                    }}
                    placeholderType="Select Type"
                    showCategory={false}
                  />
                </div>

                {isFlightLeg(leg?.type_id || safeDraft.type_id) && (
                  <div className="tp-col-6">
                    <div className="tp-field">
                      <span className="tp-label">Airline</span>
                      <select
                        value={leg?.airline || ''}
                        onChange={(e) => setDraft(prev => {
                          const cur = prev || {};
                          const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                          nextLegs[idx] = { ...nextLegs[idx], airline: e.target.value };
                          return { ...cur, legs: nextLegs };
                        })}
                        style={{ width: '100%' }}
                      >
                        <option value="">Select airline</option>
                        {AIRLINES.map((a) => (
                          <option key={a.code} value={a.name}>{a.code ? `${a.code} - ${a.name}` : a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="tp-col-6">
                  <div className="tp-grid" style={{ gap: 10 }}>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">Depart date</span>
                        <input
                          type="date"
                          value={leg?.depart_date || safeDraft.date || fallbackDate || ''}
                          onClick={openNativePicker}
                          onChange={(e) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], depart_date: e.target.value };
                            return { ...cur, legs: nextLegs };
                          })}
                        />
                      </div>
                    </div>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">Arrive day</span>
                        <select
                          value={String(leg?.arrive_day_offset ?? 0)}
                          onChange={(e) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], arrive_day_offset: Number(e.target.value) || 0 };
                            return { ...cur, legs: nextLegs };
                          })}
                        >
                          <option value="0">Same day</option>
                          <option value="1">Next day (+1)</option>
                          <option value="2">Two days later (+2)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tp-col-6">
                  <div className="tp-grid" style={{ gap: 10 }}>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">Depart (origin local)</span>
                        <input
                          type="time"
                          step="60"
                          value={leg?.depart_time || ''}
                          onClick={openNativePicker}
                          onChange={(e) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], depart_time: e.target.value };
                            return { ...cur, legs: nextLegs };
                          })}
                        />
                      </div>
                    </div>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">Arrive (destination local)</span>
                        <input
                          type="time"
                          step="60"
                          value={leg?.arrive_time || ''}
                          onClick={openNativePicker}
                          onChange={(e) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], arrive_time: e.target.value };
                            return { ...cur, legs: nextLegs };
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tp-col-6">
                  <div className="tp-grid" style={{ gap: 10 }}>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">From</span>
                        <AddressSuggestInput
                          value={leg?.from_address || ''}
                          placeholder="From (address or place)"
                          lang="en"
                          onChange={(v) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], from_address: v };
                            return { ...cur, legs: nextLegs };
                          })}
                          onSelect={(s) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = {
                              ...nextLegs[idx],
                              from_address: s.display_name || s.label || '',
                              from_lat: s.lat,
                              from_lon: s.lon,
                              from_city: cityKeyFromText(s.label || s.display_name),
                            };
                            return { ...cur, legs: nextLegs };
                          })}
                        />
                      </div>
                    </div>
                    <div className="tp-col-6">
                      <div className="tp-field">
                        <span className="tp-label">To</span>
                        <AddressSuggestInput
                          value={leg?.to_address || ''}
                          placeholder="To (address or place)"
                          lang="en"
                          onChange={(v) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = { ...nextLegs[idx], to_address: v };
                            return { ...cur, legs: nextLegs };
                          })}
                          onSelect={(s) => setDraft(prev => {
                            const cur = prev || {};
                            const nextLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
                            nextLegs[idx] = {
                              ...nextLegs[idx],
                              to_address: s.display_name || s.label || '',
                              to_lat: s.lat,
                              to_lon: s.lon,
                              to_city: cityKeyFromText(s.label || s.display_name),
                            };
                            return { ...cur, legs: nextLegs };
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {idx < legs.length - 1 && (() => {
                const curArr = toMinutes(String(leg?.arrive_time || ''));
                const next = legs[idx + 1] || {};
                const nextDep = toMinutes(String(next?.depart_time || ''));
                if (curArr == null || nextDep == null) return null;
                const curDepartDate = String(leg?.depart_date || safeDraft.date || fallbackDate || '').slice(0, 10);
                const curArriveDate = addDays(curDepartDate, Number(leg?.arrive_day_offset) || 0);
                const nextDepartDate = String(next?.depart_date || safeDraft.date || fallbackDate || '').slice(0, 10);
                const dayDelta = diffDays(curArriveDate, nextDepartDate);
                const minutes = (dayDelta * 1440) + (nextDep - curArr);

                const aIsFlight = typeNameById(leg?.type_id).trim().toLowerCase() === 'flight';
                const bIsFlight = typeNameById(next?.type_id).trim().toLowerCase() === 'flight';
                const kind = (aIsFlight && bIsFlight) ? 'Layover' : 'Connection';

                return (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tp-muted)' }}>
                    {kind}: {formatDurationMinutes(minutes)}
                  </div>
                );
              })()}
            </div>
          ))}

          <button
            type="button"
            className="tp-btn--ghost"
            onClick={() => setDraft(prev => {
              const cur = prev || {};
              const currentLegs = Array.isArray(cur.legs) ? [...cur.legs] : [];
              const last = currentLegs[currentLegs.length - 1] || {};
              currentLegs.push({
                leg_index: currentLegs.length,
                type_id: String(cur.type_id || ''),
                airline: '',
                from_address: last.to_address || '',
                from_lat: last.to_lat ?? null,
                from_lon: last.to_lon ?? null,
                from_city: last.to_city || '',
                to_address: '',
                to_lat: null,
                to_lon: null,
                to_city: '',
                depart_date: String(cur.date || fallbackDate || '').slice(0, 10),
                depart_time: '',
                arrive_day_offset: 0,
                arrive_time: '',
              });
              return { ...cur, legs: currentLegs };
            })}
          >
            Add leg
          </button>
        </div>
      ) : (
        <div className="tp-grid" style={{ gap: 10 }}>
          {isFlightLeg(safeDraft.type_id) && (
            <div className="tp-col-6">
              <div className="tp-field">
                <span className="tp-label">Airline</span>
                <select
                  value={safeDraft.airline || ''}
                  onChange={(e) => setDraft(prev => ({ ...(prev || {}), airline: e.target.value }))}
                  style={{ width: '100%' }}
                >
                  <option value="">Select airline</option>
                  {AIRLINES.map((a) => (
                    <option key={a.code} value={a.name}>{a.code ? `${a.code} - ${a.name}` : a.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <div className="tp-col-6">
            <div className="tp-field">
              <span className="tp-label">From</span>
              <AddressSuggestInput
                value={safeDraft.from_address || ''}
                placeholder="From (address or place)"
                lang="en"
                onChange={(v) => setDraft(prev => ({ ...(prev || {}), from_address: v }))}
                onSelect={(s) => setDraft(prev => ({
                  ...(prev || {}),
                  from_address: s.display_name || s.label || '',
                  from_lat: s.lat,
                  from_lon: s.lon,
                  from_city: cityKeyFromText(s.label || s.display_name),
                }))}
              />
            </div>
          </div>
          <div className="tp-col-6">
            <div className="tp-field">
              <span className="tp-label">To</span>
              <AddressSuggestInput
                value={safeDraft.to_address || ''}
                placeholder="To (address or place)"
                lang="en"
                onChange={(v) => setDraft(prev => ({ ...(prev || {}), to_address: v }))}
                onSelect={(s) => setDraft(prev => ({
                  ...(prev || {}),
                  to_address: s.display_name || s.label || '',
                  to_lat: s.lat,
                  to_lon: s.lon,
                  to_city: cityKeyFromText(s.label || s.display_name),
                }))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
